import { MutableRefObject, useState, useEffect, useCallback } from 'react'
import { useWindowSize } from '@/hooks/useWindowSize'
import { useInterval } from '@/hooks/static/useInterval'

// very important that the callback is a useCallback
export const useRefResizeEffect = (
    ref: MutableRefObject<unknown>,
    callback: () => boolean
): boolean => {
    const [isRefAttached, setIsRefAttached] = useState<boolean>(false)
    const { width, height } = useWindowSize()
    const [hasSuccessfullyRunCallback, setHasSuccessfullyRunCallback] =
        useState<boolean>(false)

    const checkIfRefAttached = useCallback((): void => {
        if (ref.current && !isRefAttached) {
            setIsRefAttached(true)
        }
    }, [isRefAttached, ref])

    useEffect(() => {
        checkIfRefAttached()
    }, [checkIfRefAttached, isRefAttached, ref])

    useInterval(
        () => {
            checkIfRefAttached()
        },
        !isRefAttached ? 100 : null
    )

    const recursivelyRunCallbackUntilSuccess = useCallback((): void => {
        const result = callback()
        if (!result) {
            setTimeout(() => {
                recursivelyRunCallbackUntilSuccess()
            }, 100)
        } else {
            setHasSuccessfullyRunCallback(true)
        }
    }, [callback])

    useEffect(() => {
        if (isRefAttached) {
            recursivelyRunCallbackUntilSuccess()
        }
    }, [recursivelyRunCallbackUntilSuccess, isRefAttached, width, height])

    return hasSuccessfullyRunCallback
}
